// src/pages/profesionales/VerProfesional.js
import React, { useEffect, useState } from 'react'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Table from 'react-bootstrap/Table'
import Tab from 'react-bootstrap/Tab'
import Tabs from 'react-bootstrap/Tabs'
import Button from 'react-bootstrap/Button'
import Spinner from 'react-bootstrap/Spinner'
import Badge from 'react-bootstrap/Badge'
import { toast } from 'react-toastify'
import { Offcanvas } from 'react-bootstrap'
import { PanelFooter, PanelHeader } from 'components/Panel'
import api from 'api/api'
import DatosPersonalesUsuario from 'components/DatosPersonalesUsuario/DatosPersonalesUsuario'
import Experiencias from 'components/profesionales/Experiencias/Experiencias'
import Cursos from 'components/profesionales/Cursos/Cursos'
import Servicios from 'components/profesionales/Servicios/Servicios'
import Facturacion from 'components/profesionales/Facturacion/Facturacion'
import MetodoCobro from 'components/profesionales/MetodoCobro/MetodoCobro'
import Insignias from 'components/profesionales/Insignias/Insignias'
import CiudadesOperacion from 'components/profesionales/CiudadesOperacion/CiudadesOperacion'
import Calificaciones from 'components/profesionales/Calificaciones/Calificaciones'

import './VerProfesional.css'
import { NO_AVATAR_IMAGE_URL } from 'utils/constants'

function VerProfesional(props) {
	const { profesionalId, setQueMostrar } = props

	const [loading, setLoading] = useState(false)
	const [profesional, setProfesional] = useState(null)

	// Datos para componente de información personal
	const datosPersonales = profesional ? {
		id: profesional.id,
		avatar: profesional.avatar,
		usuario: profesional.usuario,
		nombre: profesional.nombre,
		apellido: profesional.apellido,
		ci: profesional.ci,
		telefono: profesional.telefono,
		fechaSolicitud: profesional.fechaSolicitud,
		fechaAprobacion: profesional.fechaAprobacion,
		estado: profesional.estado,
		ubicacionProfesional: profesional.ubicacionProfesional
	} : {}

	const refreshProfesional = async () => {
		try {
			setLoading(true)
			const { datos } = await api.get('a/profesionales/' + profesionalId)
			setProfesional(datos)
		} catch (error) {
			toast.error('Error de conexión')
			console.error(error)
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		if (!loading) {
			refreshProfesional()
		}
	}, [])

	const handleClose = () => {
		setQueMostrar('')
	}

	// Renderizar el indicador de estado
	const renderEstadoBadge = () => {
		if (!profesional) return null

		let variant = 'primary'
		let text = 'Activo'

		switch (profesional.estado) {
			case 'pendiente':
				variant = 'warning'
				text = 'Pendiente'
				break
			case 'suspendido':
				variant = 'danger'
				text = 'Suspendido'
				break
			case 'rechazado':
				variant = 'secondary'
				text = 'Rechazado'
				break
			default:
				break
		}

		return <Badge bg={variant}>{text}</Badge>
	}

	// Renderizar resumen inicial con avatar y datos clave
	const renderResumenInicial = () => {
		if (!profesional) return null

		return (
			<div className="resumen-profesional">
				<Row className="align-items-center mb-4">
					<Col xs={12} md={3} className="text-center mb-3 mb-md-0">
						<img
							className="avatar-grande"
							src={profesional.avatar ? (process.env.REACT_APP_API_URL + profesional.avatar.url) : NO_AVATAR_IMAGE_URL}
							alt={`Avatar de ${profesional.nombre}`}
						/>
					</Col>
					<Col xs={12} md={9}>
						<h3 className="nombre-profesional mb-2">
							{profesional.nombre} {profesional.apellido} {renderEstadoBadge()}
						</h3>
						<div className="email-profesional mb-2">
							<i className="fa fa-envelope me-2"></i> {profesional.usuario}
						</div>
						<div className="telefono-profesional mb-2">
							<i className="fa fa-phone me-2"></i> {profesional.telefono || 'No especificado'}
						</div>
						{profesional.calificacion && (
							<div className="calificacion-profesional">
								<div className="estrellas">
									{[...Array(5)].map((_, i) => (
										<i
											key={i}
											className={`fa fa-star ${i < Math.round(profesional.calificacion) ? 'activa' : ''}`}
										></i>
									))}
								</div>
								<span className="puntaje">{profesional.calificacion.toFixed(1)}</span>
								<span className="servicios">({profesional.numServicios || 0} servicios)</span>
							</div>
						)}
					</Col>
				</Row>
			</div>
		)
	}

	// render
	return (
		<Offcanvas show={true} onHide={handleClose} placement="end" scroll="true" responsive="lg">
			<PanelHeader onClick={handleClose} iconClass="fa-arrow-left" btnText="Atrás" text="Ficha de profesional" />
			<Offcanvas.Body id="ver-profesional">
				{loading ? (
					<Spinner
						animation="border"
						size="lg"
						role="status"
						aria-hidden="true"
						variant="primary"
						className="my-3"
					/>
				) : (
					<>
						{renderResumenInicial()}

						<Tabs defaultActiveKey="datos-personales" className="mb-3">
							<Tab eventKey="datos-personales" title="Datos personales">
								<DatosPersonalesUsuario
									key={datosPersonales.id}
									{...datosPersonales}
								/>
							</Tab>
							<Tab eventKey="servicios" title="Servicios ofrecidos">
								{profesional?.servicios && profesional.servicios.length > 0 ? (
									<Servicios key={profesional.id} servicios={profesional.servicios} />
								) : (
									<div className="text-center p-4">No hay servicios registrados</div>
								)}
							</Tab>
							<Tab eventKey="experiencias" title="Experiencia">
								{profesional?.experiencias && profesional.experiencias.length > 0 ? (
									<Experiencias key={profesional.id} experiencias={profesional.experiencias} />
								) : (
									<div className="text-center p-4">No hay experiencias registradas</div>
								)}
							</Tab>
							<Tab eventKey="cursos" title="Formación">
								{profesional?.cursos && profesional.cursos.length > 0 ? (
									<Cursos key={profesional.id} cursos={profesional.cursos} />
								) : (
									<div className="text-center p-4">No hay cursos registrados</div>
								)}
							</Tab>
							<Tab eventKey="facturacion" title="Facturación">
								{profesional?.facturacion ? (
									<Facturacion key={profesional.id} datos={profesional.facturacion} />
								) : (
									<div className="text-center p-4">No hay datos de facturación</div>
								)}
							</Tab>
							<Tab eventKey="metodo-cobro" title="Método de cobro">
								{profesional?.metodoCobro ? (
									<MetodoCobro key={profesional.id} datos={profesional.metodoCobro} />
								) : (
									<div className="text-center p-4">No hay método de cobro registrado</div>
								)}
							</Tab>
							<Tab eventKey="insignias" title="Insignias">
								{profesional?.insignias && profesional.insignias.length > 0 ? (
									<Insignias
										key={profesional.id}
										insignias={profesional.insignias}
										profesionalId={profesional.id}
										onInsigniaUpdated={refreshProfesional}
									/>
								) : (
									<div className="text-center p-4">No hay insignias registradas</div>
								)}
							</Tab>
							<Tab eventKey="cobertura" title="Cobertura">
								{profesional?.ciudades && profesional.ciudades.length > 0 ? (
									<CiudadesOperacion key={profesional.id} ciudades={profesional.ciudades} />
								) : (
									<div className="text-center p-4">No hay ciudades de operación registradas</div>
								)}
							</Tab>
							<Tab eventKey="calificaciones" title="Calificaciones">
								{profesional?.calificaciones ? (
									<Calificaciones key={profesional.id} datos={profesional.calificaciones} />
								) : (
									<div className="text-center p-4">No hay calificaciones registradas</div>
								)}
							</Tab>
						</Tabs>
					</>
				)}
			</Offcanvas.Body>
			<PanelFooter>
				<Button variant="outline-primary" onClick={handleClose} className="me-2">
					Cerrar
				</Button>
			</PanelFooter>
		</Offcanvas>
	)
}

export default VerProfesional